import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { filterObjectByKeys } from '@/utils/qsHelper';
import AddIcon from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AddNonMemberTargetSummaryFilterSection from './AddNonMemberTargetSummaryFilterSection';
import { useGetUser, useGetUserResultType } from '@/hooks/useGetUser';
import dayjs from 'dayjs';
import { DISPLAY_CHINESE_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
	useAddSurveyFreeUserMutation,
	useGetSurveyFreeJoinPageQuery,
} from '@/api/survey/surveyApiSlice';
import {
	FreeUserPageReqDTO,
	FreeUserPageResDTO,
} from '@/api/DTO/survey/survey.interface';
import {
	getUserStatusChipColor,
	getUserStatusLabel,
	UserStatus,
} from '@/api/enum/userStatus.enum';

export interface FreeUserPageDataGrid {
	id: string;
	userId: string;
	email: string;
	lastLogin?: string;
	username?: string;
	phone?: string;
	nickname: string;
	district: string;
	status?: UserStatus;
}

const AddNonMemberTargetListModal = () => {
	const { t } = useTranslation();
	const { surveyId } = useParams();

	const [open, setOpen] = useState(false);

	const [addSurveyFreeUser] = useAddSurveyFreeUserMutation();

	const handleClose = () => {
		setOpen(false);
	};

	const { userRoles } = useGetUser<useGetUserResultType>({
		shouldRefetch: true,
		roleObj: {
			read: ['read', 'user', 'user-list'],
			create: ['create', 'user'],
		},
	});

	const [selectedUser, setSelectedUser] = useState<string[]>([]);

	const defaultFreeUserPageFilter: FreeUserPageReqDTO = {
		pagination: initialPagination,
		sortings: [],
		loginID: '',
		surveyId: surveyId ?? '',
	};

	const defaultValues: DefaultValues<FreeUserPageReqDTO> =
		defaultFreeUserPageFilter;

	const [searchFilter, setSearchFilter] = useState<FreeUserPageReqDTO>(
		defaultFreeUserPageFilter
	);

	const methods = useForm<FreeUserPageReqDTO>({
		defaultValues,
	});

	const transformedSearchParams: FreeUserPageReqDTO = {
		...defaultFreeUserPageFilter,
		...filterObjectByKeys(searchFilter, [
			'pagination',
			'sortings',
			'loginID',
			'statuses',
			'districtIds',
			'surveyId',
		]),
	};

	const {
		data: freeUserPageSummary,
		isSuccess: isGetFreeUserPageSummarySuccess,
	} = useGetSurveyFreeJoinPageQuery(transformedSearchParams, {
		refetchOnMountOrArgChange: true,
		skip: !userRoles.read,
	});

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as FreeUserPageReqDTO;
			setSearchFilter(propertyFilterFormResult);
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformResponse = useCallback((response?: FreeUserPageResDTO[]) => {
		if (response == null) return;
		const newResponse: FreeUserPageDataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				id: item.userId,
				userId: item.userId,
				email: item.email,
				lastLogin: item.lastLogin,
				username: item.username ?? '-',
				nickname: item.nickname ?? '-',
				phone: item.phone,
				district: item.district,
				status: item.status,
			});
		});
		return newResponse;
	}, []);

	const columns: GridColDef[] = [
		{
			field: 'username',
			headerName: t('common.username'),
			flex: 25,
			renderCell: (params) => (
				<Box>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='body2'
						variant='regular'
					>
						{params.row.username}
					</Typography>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='caption'
						color='common.neutral50'
						variant='regular'
					>
						{params.row.phone}
					</Typography>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='caption'
						color='common.neutral50'
						variant='regular'
					>
						{params.row.email}
					</Typography>
				</Box>
			),
		},
		{
			field: 'district',
			headerName: t('common.belongedDistrict'),
			flex: 20,
		},
		{
			field: 'status',
			headerName: t('common.status'),
			flex: 10,
			renderCell: (params) => (
				<Chip
					label={getUserStatusLabel(params.row.status)}
					color={getUserStatusChipColor(params.row.status)}
					variant='outlined'
				/>
			),
		},
		{
			field: 'lastLogin',
			headerName: t('common.lastOnlineTime'),
			flex: 20,
			renderCell: (params) => (
				<Typography variant='regular'>
					{params.row.lastLogin
						? dayjs(params.row.lastLogin)
								.locale('zh-hk-my-setting')
								.format(DISPLAY_CHINESE_DATE_TIME_FORMAT)
						: '-'}
				</Typography>
			),
		},
	];

	const handleAddSurveyFreeUser = async () => {
		const response = await addSurveyFreeUser({
			surveyId: surveyId ?? '',
			userIds: selectedUser,
		});

		if ('data' in response) {
			enqueueSnackbar(t('snackbar.addNonMemberTargetSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
			setSelectedUser([]);
		}
	};

	return (
		<>
			<Button
				variant='contained'
				startIcon={<AddIcon />}
				onClick={() => {
					setOpen(true);
				}}
			>
				{t('button.addNonMemberTarget')}
			</Button>

			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('survey.addSurveyTarget') + t('common.nonMemberUser')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent
					sx={{
						minHeight: '765px',
					}}
				>
					<Typography
						variant='body1'
						sx={{ whiteSpace: 'pre-line' }}
						marginBottom={2}
					>
						{t('survey.addSurveyTargetMsg')}
					</Typography>
					<FormProvider {...methods}>
						<AddNonMemberTargetSummaryFilterSection />

						<Box marginTop={2} minWidth={'800px'}>
							<AppDataGrid
								loading={!isGetFreeUserPageSummarySuccess}
								rows={transformResponse(freeUserPageSummary?.data?.items) ?? []}
								columns={columns}
								rowCount={freeUserPageSummary?.data?.pagination.totalItems ?? 0}
								getRowId={(row) => row.id}
								checkboxSelection
								selectionModel={selectedUser}
								onSelectionModelChange={(ids) => {
									setSelectedUser(ids.map((id) => String(id)));
								}}
							/>
						</Box>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						color='primary'
						onClick={handleAddSurveyFreeUser}
						disabled={selectedUser.length === 0}
					>
						{t('button.add')}
						{selectedUser.length > 0 && `(${selectedUser.length})`}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AddNonMemberTargetListModal;
